/**
 * Center-delegated shipment creation — center creates a shipment on
 * behalf of a customer who's standing at the counter, with the
 * customer's OTP-based consent.
 *
 *   Server: cargoCenters.js → /api/cargo/centers/me/delegated-shipments/*
 *   Service: cargoCenterDelegatedShipmentService.js
 *
 * Three-step flow:
 *   1. startConsent  — center submits customer phone, server sends OTP
 *   2. verifyConsent — center types the code customer reads aloud
 *   3. create        — center submits shipment payload + consent token
 */
import { api } from './client';
import { unwrap } from './_unwrap';

export interface StartConsentInput { phoneNumber: string; language?: 'ar' | 'en' | 'tr' }
export interface StartConsentResult {
  consentRequestId: number;
  channel: 'sms' | 'whatsapp';
  expiresAt: string;
  customerPhone: string;
  centerName: string;
}

export interface VerifyConsentInput { consentRequestId: number; code: string }
export interface VerifyConsentResult {
  consentToken: string;
  consentTokenExpiresAt: string;
  consentRequestId: number;
  customerPhone: string;
}

export interface CreateDelegatedShipmentInput {
  consentToken: string;
  consentRequestId: number;
  shipment: {
    senderCityId?: number;
    pickupAddress?: string;
    pickupLat?: number;
    pickupLng?: number;
    pickupCity?: string;
    receiverCityId?: number;
    receiverName: string;
    receiverPhone: string;
    receiverAddress?: string;
    receiverCity?: string;
    receiverLat?: number;
    receiverLng?: number;
    packageType?: string;
    packageDescription: string;
    weight?: number;
    quantity?: number;
    estimatedValue?: number;
    isFragile?: boolean;
    requiresRefrigeration?: boolean;
    notes?: string;
    paymentMethod?: string;
    paidBy?: 'sender' | 'receiver';
    images?: string[];
    [key: string]: any;
  };
}

export interface DelegatedShipmentRow {
  id: number;
  tracking_number: string;
  short_code: string;
  status: string;
  created_at: string;
  sender_id: string;
  sender_name?: string;
  sender_phone?: string;
  receiver_name?: string;
  receiver_phone?: string;
  pickup_city?: string;
  receiver_city?: string;
  price?: number;
  currency?: string;
  created_by_center_operator_id?: string;
  center_consent_at?: string;
}

export interface DelegatedShipmentsList {
  center: { id: number; name: string };
  rows: DelegatedShipmentRow[];
}

export const centerDelegatedApi = {
  async startConsent(input: StartConsentInput): Promise<StartConsentResult> {
    const r = await api.post('/api/cargo/centers/me/delegated-shipments/start-consent', input);
    return unwrap<StartConsentResult>(r);
  },
  async verifyConsent(input: VerifyConsentInput): Promise<VerifyConsentResult> {
    const r = await api.post('/api/cargo/centers/me/delegated-shipments/verify-consent', input);
    return unwrap<VerifyConsentResult>(r);
  },
  async create(input: CreateDelegatedShipmentInput): Promise<{ shipment: any; customerUserId: string; centerName: string }> {
    const r = await api.post('/api/cargo/centers/me/delegated-shipments/create', input);
    return unwrap(r);
  },
  async listMine(params?: { limit?: number; offset?: number }): Promise<DelegatedShipmentsList> {
    const r = await api.get('/api/cargo/centers/me/delegated-shipments', { params });
    return unwrap<DelegatedShipmentsList>(r);
  },
};
